/**
 * The rare things — one per several square kilometres, findable, named, and
 * standing where the world has always had them.
 *
 * ## Why this is a separate class from litter
 *
 * `world/litter.ts` grows sticks, stones, clumps and deadfall on a 2 m lattice:
 * hundreds per chunk, described rather than named ("a fallen branch"), and
 * thinned hard by the render budget. That is *texture*. It answers "what is
 * underfoot", and the honest answer is usually "nothing worth crossing a
 * valley for".
 *
 * A relic answers a different question: **is there anything out there?** It is
 * sparse enough that meeting one is an event, pointable from far enough away
 * that it can draw you off a course, and named individually so that two
 * players can mean the same one. `world/landmarks.ts` already does exactly
 * this for geography — 403 waterfalls, passes, crater lakes and isolated
 * groves, found by one 34 ms scan at load with **nothing stored** — and this
 * is that pattern applied to things small enough to stand next to.
 *
 * ## The three laws it has to satisfy, and how
 *
 *  - **Law 1 — deterministic baseline.** A relic is a pure function of world
 *    position and world seed. Nothing here reads the camera, the clock, or the
 *    order chunks were visited. Two players on one island find the same relics
 *    in the same places, for ever, without either of them having been there
 *    first.
 *  - **Law 2 — baseline + sparse history.** Nothing is stored. The lattice is
 *    *invertible*: given a point you can compute which super-cell you are in
 *    and ask that cell directly, so finding the relic near you is a floor
 *    divide and a few hashes rather than a search over a list. Untouched
 *    ground costs zero bytes, exactly as litter's does. What a god or a mortal
 *    *did* to a relic is history and belongs in the existing sparse stores
 *    (`CarriedStore` if it is picked up, `ThingStore` if it is made sacred) —
 *    never here.
 *  - **Law 9 — observation writes nothing.** There is no cache, no visited
 *    set, no "discovered" flag. Finding a relic is a read. What the player has
 *    found is answered by the Chronicle, from what they *acted on* — you know
 *    a thing because you did something about it, never because you looked.
 *
 * ### Where the world seed enters, and where it deliberately does not
 *
 * The read surface `main.ts` already calls is `relicNear(x, z, reach, site)` —
 * **there is no seed in it**, on purpose: `render/relics.ts` and the pointer
 * have to elect the *same* relics down to the last hash, and any argument one
 * of them can pass that the other cannot is a pointer/picture disagreement
 * waiting to happen. So the candidate LATTICE carries a fixed module salt, and
 * every world-dependent decision — is this land, is it high enough, which
 * kinds could stand here at all — is read from `site`, which is a pure
 * function of the seed. Two islands therefore share the grid of places a relic
 * *could* be and agree about none of the places one actually is. That is law 1
 * satisfied through the site rather than through a seed argument, and it is
 * the reason `RELIC_SALT` below is a constant instead of a parameter.
 *
 * ## The grain, and why it was measured
 *
 * A super-cell of side `S` metres is `S²/1e6` km². The island carries roughly
 * 883 km² of land, so the count of candidate cells over land is `883e6 / S²`,
 * and the relic count is that times the acceptance rate of the conditions
 * gate.
 *
 * `tools/relics.ts` swept `S` over 1000–4000 m against the instrument
 * `tools/fauna.ts` established for landmarks — how many a straight flight of a
 * given length meets — and the numbers that decided `RELIC_CELL = 1600` are in
 * the constant's own note below.
 */
import { clamp, hash01, hash3, lerp, placeKey } from '@/core/rng';
import type { Site } from '@/world/types';

/**
 * Side of the election lattice, in metres.
 *
 * One relic is elected per super-cell, so this sets rarity directly. It is
 * declared here rather than inlined so `tools/relics.ts` sweeps the REAL
 * constant instead of a copy that drifts from it — the mistake `tools/repose.ts`
 * was written to avoid.
 *
 * **Measured, `npx tsx tools/relics.ts`, on the shipped island (883 km² of
 * land).** The sweep drives the real `relicIn` at each grain, so these are the
 * game's own elections and not a model of them:
 *
 * ```
 *   S      relics   per km²   walk to nearest      straight flight over land
 *                             p50      p90      met nothing   gap p50   p90
 *   1000     531     0.601   0.57 km  1.00 km        0%        1.25 km  3.15
 *   1400     276     0.313   0.79 km  1.38 km        1%        2.20 km  5.95
 *   1600     207     0.234   0.90 km  1.60 km        2%        2.80 km  7.80
 *   2000     120     0.136   1.18 km  2.19 km        7%        4.20 km 12.40
 *   2800      61     0.069   1.68 km  3.14 km       19%        6.60 km 20.65
 *   4000      23     0.026   2.81 km  6.20 km       52%       13.85 km 29.35
 * ```
 *
 * The bar is what this class exists to fix. `tools/fauna.ts` measured **71% of
 * straight flights over land meeting nothing at all** among the things people
 * had made, and a median 6.55 km to the next thing worth looking at. Relics
 * have to *thicken* that answer without becoming a second layer of texture.
 *
 * 1600 is the knee of the sweep. Below it the count doubles per step and a
 * relic stops being rare — 1000 m puts one within 570 m of you at the median,
 * which is nearer than the next camp. Above it the flight collapses: at 2000
 * the p90 gap is already 12.4 km and at 2800 nearly a fifth of all crossings
 * meet nothing, which is the disease coming back.
 *
 * At 1600 one relic is **0.90 km from the next at the median** — eleven minutes
 * on foot, two and a half seconds at survey speed — and 98% of straight
 * crossings meet at least one. Measured against the landmarks it thickens
 * rather than repeats: p90 flight gap 5.35 → 3.25 km, worst 24.85 → 13.20 km,
 * and the walk to the nearest of either falls from 0.76 to 0.56 km. **10 of the
 * 207 stand within 200 m of a landmark and 53 within 500 m** — the other three
 * quarters are somewhere the map was not already sending you, which is the
 * whole point of the class.
 */
export const RELIC_CELL = 1600;

/**
 * The fixed salt of the candidate lattice — see the seed note in the header.
 * Not a world seed: the world enters through `site`.
 */
const RELIC_SALT = 0x9a17;

/** What a relic is, once found. Position is its name (law 1). */
export interface Relic {
  /** `placeKey`-style identity, so promotion and carrying key on the same string. */
  key: string;
  x: number; z: number;
  /** Metres of footprint — what a channelled verb collapses onto. */
  size: number;
  /** Which sort of rare thing. The vocabulary is owned by this module. */
  kind: RelicKind;
}

/**
 * The sorts of rare thing.
 *
 * Seven, and every one of them is a thing you would walk toward: something
 * raised, something fallen, something living, something dead, a way in, a way
 * down, and the leavings of people nobody remembers. They are a *closed* union
 * so every `switch` over them is exhaustive by construction — the day an
 * eighth is added the compiler names every place that has to learn about it.
 */
export type RelicKind =
  | 'standing_stone'
  | 'meteorite'
  | 'spring'
  | 'bone_field'
  | 'lightning_tree'
  | 'cave_mouth'
  | 'shell_midden';

/** Fixed order — the weighted election indexes it, so it may not be reordered. */
export const RELIC_KINDS: readonly RelicKind[] = [
  'standing_stone', 'meteorite', 'spring', 'bone_field',
  'lightning_tree', 'cave_mouth', 'shell_midden',
];

/**
 * Relative frequency among the kinds a place could hold.
 *
 * These are weights, not probabilities: a cell offers whichever kinds its
 * ground could carry at all, and the pick is over that subset. So a weight of
 * 0.05 does not mean one in twenty relics — it means one in twenty *of the
 * relics standing on ground that could also have held one of these*, which is
 * how a meteorite ends up being three things on a whole island rather than a
 * fixed quota. `tools/relics.ts` prints the resulting census per kind, and
 * that census is the number to tune against.
 */
const WEIGHT: Record<RelicKind, number> = {
  standing_stone: 1.00,
  meteorite: 0.055,      // the one that must stay a rumour
  spring: 0.80,
  bone_field: 0.55,
  lightning_tree: 0.75,
  cave_mouth: 0.45,
  shell_midden: 0.60,
};

/**
 * Share of qualifying cells that actually carry anything.
 *
 * Separated from the lattice grain on purpose: `RELIC_CELL` says how coarse
 * the world's memory of rare things is, and this says how much of that memory
 * is full. Sweeping them independently is what let the probe hold the density
 * fixed while changing the *clumping* — a cell that is often empty makes the
 * spacing irregular, which is what stops a lattice reading as a lattice.
 */
const ACCEPT = 0.62;

/**
 * Metres above the sea the coarse region height must clear.
 *
 * Read off `site.height`, which is the same field `render/relics.ts` reads,
 * rather than off the fine surface — the renderer and the pointer must elect
 * the identical set, and only the coarse field is available to both. Set at
 * 4 m rather than `rocks.ts`'s 1.5 m because that is a *fine* height and this
 * is a 260 m cell's: a coarse 4 m still puts a shell midden's foot on the
 * strand and keeps a standing stone out of the surf.
 */
const SEA_CLEAR = 4;

const OPEN = new Set(['grassland', 'chaparral', 'scrub', 'alpine', 'oak', 'desert']);
const DRY_OPEN = new Set(['desert', 'chaparral', 'grassland', 'scrub', 'alpine', 'beach']);
const WOODY = new Set(['redwood', 'conifer', 'oak', 'riparian']);
const ROCKY = new Set(['alpine', 'chaparral', 'desert', 'conifer', 'oak']);
const STRAND = new Set(['beach', 'marsh']);

/**
 * Could this ground hold this kind at all?
 *
 * Deliberately conditions rather than a hash: law 17 says knowledge of the
 * world is part of a god's strength, and a god who learns that springs rise
 * where the rain gathers and middens lie on the strand can *hunt* for them.
 * A relic placed by a pure hash teaches nothing and is only a lottery.
 */
function suits(kind: RelicKind, s: Site): boolean {
  switch (kind) {
    // raised, or left standing, on ground open enough to see it from
    case 'standing_stone': return OPEN.has(s.biome);
    // it fell out of the sky: nowhere on land is excluded
    case 'meteorite': return true;
    // where water is already trying to reach the surface
    case 'spring': return (s.rain >= 0.40 || s.flow >= 40) && s.height >= 20 && s.biome !== 'beach';
    // dry open country, where a carcass stays a carcass
    case 'bone_field': return DRY_OPEN.has(s.biome) && s.rain < 0.62;
    // a tall thing in country that grows tall things, on ground that catches it
    case 'lightning_tree': return WOODY.has(s.biome) && s.height >= 60;
    // rock country with enough relief to have a hole in it
    case 'cave_mouth': return ROCKY.has(s.biome) && s.height >= 180;
    // people ate here, for a very long time, and nobody remembers them
    case 'shell_midden': return STRAND.has(s.biome);
  }
}

/**
 * The greatest dimension in metres — what the picker prices pointability by
 * and what a channelled verb collapses onto.
 *
 * The rule these were picked against is that a relic reads from far enough away
 * to change where you were going, and `main.ts` prices exactly that: both
 * classes go through the same `pxOf(size, dist) >= PICK_PIXELS` gate, so metres
 * of size ARE metres of reach.
 *
 * What that is *not* is "bigger than anything on the litter lattice", which is
 * what this note used to claim. It is true of one kind of seven.
 * `npx tsx tools/relics.ts` prints the comparison:
 *
 * ```
 *   litter through litterAt: median item 0.83 m, p90 1.74 m — but the lattice
 *   deliberately carries two landmark-scale shapes, a full-length fallen log at
 *   8.16 m and the biggest fist of stone at 1.01 m
 *
 *   clears that 8.16 m log at every size   lightning_tree 9.0–19.0
 *   clears it over part of its range       meteorite 3–9, bone_field 6–15,
 *                                          cave_mouth 4–10, shell_midden 5–13
 *   never reaches it                       standing_stone 2.6–6.4, spring 2.2–5.0
 * ```
 *
 * The honest statement of the difference is the one the numbers support: every
 * relic is 2.6× to 23× the median litter item in metres and 40× to 6,900× in
 * mass, and the lattice's two landmark-scale shapes — deliberately carried out
 * to a kilometre by `nearDetail`'s band for the same reason — are the whole of
 * the overlap. Where they overlap, `main.ts` offers the relic first, on purpose
 * and with its own comment; being the only one for miles is the content, and
 * that is a fact about rarity rather than about metres.
 *
 * Exported for the same reason `relicCellOf` is: `tools/relics.ts` and
 * `test/relics.ts` both need the RANGE of a kind, and neither may re-derive it.
 */
export function sizeOf(kind: RelicKind, h: number): number {
  switch (kind) {
    case 'standing_stone': return lerp(2.6, 6.4, h);      // its height, standing
    case 'meteorite': return lerp(3.0, 9.0, h);           // the crater, not the iron
    case 'spring': return lerp(2.2, 5.0, h);              // the pool and its lip
    case 'bone_field': return lerp(6.0, 15.0, h);         // the scatter
    case 'lightning_tree': return lerp(9.0, 19.0, h);     // the snag, still upright
    case 'cave_mouth': return lerp(4.0, 10.0, h);         // the opening
    case 'shell_midden': return lerp(5.0, 13.0, h);       // the mound
  }
}

/**
 * The super-cell a point falls in. Exported because the probe and the test
 * both need to enumerate cells, and neither may re-derive the arithmetic.
 * `cell` is a parameter only so `tools/relics.ts` can sweep the grain through
 * the real code path instead of a copy of it.
 */
export const relicCellOf = (
  x: number, z: number, cell: number = RELIC_CELL,
): { sx: number; sz: number } => ({
  sx: Math.floor(x / cell),
  sz: Math.floor(z / cell),
});

/**
 * Where inside its cell a relic stands, as a fraction in [0, 1)².
 *
 * Two independent hashes, so the position is not correlated with whatever the
 * existence gate reads. Pure, and invertible in the sense that matters: you
 * never search for a relic, you compute the one cell that could hold it.
 */
export const relicOffsetIn = (sx: number, sz: number, seed: number): { u: number; v: number } => ({
  u: (hash3(sx, sz, seed + 0x5e1) % 4096) / 4096,
  v: (hash3(sx, sz, seed + 0x5e2) % 4096) / 4096,
});

/**
 * The relic elected by one super-cell, or `null`.
 *
 * The whole generator, and the only place a relic is ever decided. Three
 * hashes and one site sample: the spot, the acceptance gate, and the pick
 * among whatever that ground could hold. Nothing is stored and nothing is
 * searched.
 */
export function relicIn(
  sx: number, sz: number,
  site: (x: number, z: number) => Site,
  cell: number = RELIC_CELL,
): Relic | null {
  // the acceptance gate is read FIRST, before the site sample, because it is
  // the cheap half: at ACCEPT = 0.62 this skips a third of the sampling in
  // `relicNear`'s hot path and in the renderer's island-wide build
  if (hash01(sx, sz, RELIC_SALT + 0x11) >= ACCEPT) return null;

  const { u, v } = relicOffsetIn(sx, sz, RELIC_SALT);
  const x = (sx + u) * cell, z = (sz + v) * cell;
  const s = site(x, z);
  if (!s.land || s.height < SEA_CLEAR) return null;

  // weighted pick over the kinds this ground could hold. A running sum rather
  // than a table, so adding a kind cannot silently renumber the others: the
  // pick is `p × total`, and every kind's slice is its own weight.
  let total = 0;
  for (const k of RELIC_KINDS) if (suits(k, s)) total += WEIGHT[k];
  if (total <= 0) return null;
  let p = hash01(sx, sz, RELIC_SALT + 0x23) * total;
  let kind: RelicKind = RELIC_KINDS[0];
  for (const k of RELIC_KINDS) {
    if (!suits(k, s)) continue;
    kind = k;
    p -= WEIGHT[k];
    if (p < 0) break;
  }

  const size = sizeOf(kind, hash01(sx, sz, RELIC_SALT + 0x37));
  return { key: placeKey(x, z), x, z, size, kind };
}

/**
 * The relic nearest to a point, or `null` — the whole read surface.
 *
 * `site` is passed in rather than imported so this module never reaches for a
 * `Region` and can be driven headlessly from a probe. It is what decides
 * whether a candidate is on ground that could hold the thing at all.
 *
 * @param reach metres; how far from (x, z) to look. The caller owns this,
 *              because pointability is allowed to care where the eye is and
 *              existence is not.
 */
export function relicNear(
  x: number, z: number, reach: number,
  site: (x: number, z: number) => Site,
): Relic | null {
  const r = Math.max(0, reach);
  // the cells the reach can touch. At RELIC_CELL = 1600 m and a reach of tens
  // of metres this is one cell almost always and four at a corner — the same
  // "compute the cell, do not search" shape `litterNear` has, three orders of
  // magnitude coarser.
  const c0 = relicCellOf(x - r, z - r), c1 = relicCellOf(x + r, z + r);
  let best: Relic | null = null, bestD = Infinity;
  for (let sz = c0.sz; sz <= c1.sz; sz++) for (let sx = c0.sx; sx <= c1.sx; sx++) {
    const rel = relicIn(sx, sz, site);
    if (!rel) continue;
    const d = Math.hypot(rel.x - x, rel.z - z);
    // a thing is "under the pointer" if the pointer is inside its own
    // footprint, or within the reach it was asked for — whichever is kinder,
    // exactly as `litterNear` decides it
    if (d > Math.max(r, rel.size * 0.5)) continue;
    if (d < bestD) { bestD = d; best = rel; }
  }
  return best;
}

/**
 * Every relic on a rectangle of world, strongest-lattice-order first.
 *
 * The renderer's entry point, and the probe's. It is a *scan*, not a store:
 * `render/relics.ts` calls it once at load exactly as `buildRocks` walks the
 * region grid once, and nothing is retained here afterwards. Deterministic in
 * scan order, so two machines build the list in the same order.
 */
export function relicsIn(
  x0: number, z0: number, x1: number, z1: number,
  site: (x: number, z: number) => Site,
  cell: number = RELIC_CELL,
): Relic[] {
  const a = relicCellOf(x0, z0, cell), b = relicCellOf(x1, z1, cell);
  const out: Relic[] = [];
  for (let sz = a.sz; sz <= b.sz; sz++) for (let sx = a.sx; sx <= b.sx; sx++) {
    const rel = relicIn(sx, sz, site, cell);
    if (rel) out.push(rel);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Names.
//
// §12 says the world is the interface and §8 wants places to carry names. An
// unnamed lump is scenery; "the Broken Watcher" is somewhere you went, and two
// players can mean the same one without either of them having to be told an id.
//
// Pure functions of (position) like everything else here, so the name is the
// same on every machine for ever and costs nothing to store — the same trick
// that makes a tree's position its name.
//
// `landmarks.ts` resolves its name collisions with a global pass over the whole
// island's list, which this class cannot do: a relic is computed from ONE cell
// with no knowledge of any other, and that is the property that makes finding
// one a floor divide. So the space is widened instead of deduplicated.
//
// Widened TWICE, because the first cut was measured and was not enough. At
// 32 epithets × 12 nouns × 7 suffixes `tools/relics.ts` still printed three
// duplicated names over 207 relics — the commonest kind carries 71 of them, and
// 71² / 2N is not small against a space that a third of the relics do not use
// because their suffix slot came up empty. At 16 nouns and 12 suffixes the same
// probe prints **205 distinct names over 207 relics** — two collisions, both
// between relics tens of kilometres apart — and `test/relics.ts` asserts that
// count rather than trusting the arithmetic.

const EPITHET = [
  'Grey', 'Old', 'Long', 'Broken', 'Silent', 'Cold', 'Hidden', 'Black',
  'White', 'Crooked', 'Still', 'Bright', 'Deep', 'Bare', 'Far', 'Green',
  'Sunken', 'Narrow', 'Bitter', 'Quiet', 'Red', 'Lost', 'Sharp', 'Wide',
  'Sweet', 'Wild', 'Blind', 'Patient', 'Nameless', 'Weeping', 'Iron', 'Salt',
] as const;

const NOUN: Record<RelicKind, readonly string[]> = {
  standing_stone: ['Stone', 'Finger', 'Watcher', 'Sentinel', 'Pillar', 'Mark',
                   'Elder', 'Warden', 'Post', 'Tooth', 'Herald', 'Keeper',
                   'Reckoning', 'Waiting', 'Hand', 'Oath'],
  meteorite:      ['Star', 'Iron', 'Cinder', 'Visitor', 'Fall', 'Ember',
                   'Wound', 'Guest', 'Seed', 'Nail', 'Shard', 'Coal',
                   'Arrival', 'Bruise', 'Answer', 'Errand'],
  spring:         ['Spring', 'Eye', 'Well', 'Mouth', 'Rise', 'Font',
                   'Source', 'Cup', 'Whisper', 'Vein', 'Throat', 'Gift',
                   'Patience', 'Kindness', 'Beginning', 'Murmur'],
  bone_field:     ['Bones', 'Ribs', 'Yard', 'Ground', 'Rest', 'Fall',
                   'Gathering', 'Company', 'Choir', 'Silence', 'Remains', 'Table',
                   'Assembly', 'Winter', 'Reckoning', 'Harvest'],
  lightning_tree: ['Snag', 'Torch', 'Mast', 'Spar', 'Widow', 'Scar',
                   'Candle', 'Pillar', 'Blaze', 'Stump', 'Finger', 'Nail',
                   'Sermon', 'Argument', 'Refusal', 'Warning'],
  cave_mouth:     ['Mouth', 'Throat', 'Door', 'Hollow', 'Dark', 'Gape',
                   'Way', 'Cellar', 'Under', 'Gullet', 'Den', 'Step',
                   'Invitation', 'Question', 'Descent', 'Larder'],
  shell_midden:   ['Heap', 'Midden', 'Table', 'Feast', 'Mound', 'Kitchen',
                   'Shells', 'Leavings', 'Bank', 'Hill', 'Store', 'Memory',
                   'Century', 'Appetite', 'Custom', 'Thanks'],
};

/** Mostly nothing: a bare two-word name is the register, and the tail is relief. */
const SUFFIX = [
  '', '', '',
  'of the Ash', 'of the Ford', 'of the Long Night', 'of the First Rain',
  'of the Old Road', 'of the Turning', 'of the Dry Year', 'of the Two Winters',
  'of the Last Crossing',
] as const;

/** The name this relic carries. Deterministic from position (law 1). */
export function relicName(r: Relic): string {
  const kx = Math.round(r.x), kz = Math.round(r.z);
  const nouns = NOUN[r.kind];
  const e = EPITHET[Math.floor(hash01(kx, kz, 0x1a7) * EPITHET.length) % EPITHET.length];
  const n = nouns[Math.floor(hash01(kz, kx, 0x2b9) * nouns.length) % nouns.length];
  const s = SUFFIX[Math.floor(hash01(kx ^ 0x55, kz, 0x3c1) * SUFFIX.length) % SUFFIX.length];
  return s ? `the ${e} ${n} ${s}` : `the ${e} ${n}`;
}

/**
 * What the world says when you point at one.
 *
 * Kept beside the lattice rather than in `ui/attention.ts` so that adding a
 * relic kind is one file's edit. Returns the two-clause shape the rest of the
 * chrome uses: what it is, and one true thing about it.
 *
 * The note states and never explains — law 10. A lightning-struck tree is
 * *"the fire went through it and stopped"*, not "struck by lightning in year
 * 412 by a god": nothing witnessable may carry whose hand it was, and a relic
 * is the most tempting place in the world to break that.
 */
export function nameRelic(r: Relic): { name: string; note?: string } {
  const name = relicName(r);
  switch (r.kind) {
    case 'standing_stone':
      return { name, note: 'somebody stood this on end, and nobody remembers why' };
    case 'meteorite':
      return { name, note: 'it came down here, and the ground still shows it' };
    case 'spring':
      return { name, note: 'water comes out of the ground here, all year' };
    case 'bone_field':
      return { name, note: 'a great many animals died in one place' };
    case 'lightning_tree':
      return { name, note: 'the fire went through it and stopped' };
    case 'cave_mouth':
      return { name, note: 'the hill is open here, and it goes back further than you can see' };
    case 'shell_midden':
      return { name, note: 'people ate here for a hundred years and left the shells' };
  }
}

/**
 * Roughly what a relic weighs, in kilogrammes — the price Grasp reads.
 *
 * Design IV §25 prices Grasp by size, mass and attachment. Every one of these
 * is deliberately at or beyond the top of what a young god can lift: a relic
 * is *leverage's* problem, not a pebble's, and law 17 says working against
 * conditions is dear. The two that are not solid mass — a spring and a cave
 * mouth — return the mass of what would have to be moved to move THEM, which
 * is a hillside, and that is the honest answer rather than a special case.
 */
export function relicMass(kind: RelicKind, size: number): number {
  const s = Math.max(0.5, size);
  switch (kind) {
    // a slab standing on end: roughly s tall, s/3 wide, s/6 thick, in rock
    case 'standing_stone': return (s * s * s / 18) * 2600;
    // the iron itself is a fraction of the crater it made
    case 'meteorite': return (Math.PI / 6) * (s * 0.3) ** 3 * 7800;
    // dead standing timber, a tenth as thick as it is tall
    case 'lightning_tree': return Math.PI * (s / 20) ** 2 * s * 500;
    // a mound of shell and midden earth: a low cone
    case 'shell_midden': return (Math.PI / 3) * (s / 2) ** 2 * (s * 0.22) * 900;
    // Scattered bone over a wide patch, priced by AREA rather than volume,
    // because that is the shape it is. The coefficient is an ESTIMATE and not
    // a measurement: a large grazer's dry skeleton is of the order of 40 kg,
    // "a great many animals" is dozens of them, and the scatter runs 6–15 m
    // across — which lands somewhere near 25 kg per square metre.
    //
    // It is the LIGHTEST thing in the class and it does not clear the litter
    // lattice's two landmark-scale shapes. `npx tsx tools/relics.ts`:
    // the smallest bone field is 6 m and 900 kg, against a full-length fallen
    // log at 2,560 kg and the biggest fist of stone at 1,394 kg. (An earlier
    // cut of this note said the coefficient had been raised from 12 to close
    // exactly that gap. It did not: 12 → 25 took the smallest field from 432 kg
    // to 900 kg, and clearing the log would need 71 kg/m², which is three times
    // what the estimate above can support. Tuning a physical estimate until an
    // assertion goes green is the wrong half to move.)
    //
    // So the class boundary is stated where it is true, and `test/relics.ts` §7
    // asserts it there: against what litter USUALLY is. The tool's 6,000-item
    // sweep puts the median litter item at 22 kg (the suite's own smaller sweep
    // says 24), so the lightest relic the vocabulary can produce outweighs it
    // by 37–40×. `standing_stone` at its 2.6 m minimum (2,539 kg) is under that
    // log too, by a hair; the suite names both exceptions rather than hiding
    // them behind an average.
    case 'bone_field': return s * s * 25;
    // you do not lift these; you would be lifting the hill
    case 'spring':
    case 'cave_mouth': return (s * s * s) * 2400;
  }
}

/**
 * How hard the world holds on, 0..1.
 *
 * `world/litter.ts`'s twig is 0.05 because a twig is attached to nothing.
 * Everything here is the opposite end of the same scale: a relic is *of* its
 * place, and the two that are holes in the ground cannot be detached from it
 * at all.
 */
export function relicAttachment(kind: RelicKind): number {
  switch (kind) {
    case 'standing_stone': return 0.72;   // set into a socket, on purpose
    case 'meteorite': return 0.55;        // buried by its own arrival
    case 'lightning_tree': return 0.80;   // still rooted, and dead roots do not let go
    case 'shell_midden': return 0.60;     // a century of it, packed
    case 'bone_field': return 0.30;       // lying loose, but there is a lot of it
    case 'spring':
    case 'cave_mouth': return 1;          // it is not a thing, it is a place
  }
}

/**
 * How tall the thing stands above the ground it is on, in metres.
 *
 * Shared with `render/relics.ts` so the drawn silhouette and the pointer's
 * footprint cannot drift, and clamped positive so nothing here can ever seat a
 * mesh below the surface.
 */
export function relicHeight(kind: RelicKind, size: number): number {
  switch (kind) {
    case 'standing_stone': return size;              // `size` IS its height
    case 'lightning_tree': return size;              // likewise, a standing snag
    case 'meteorite': return clamp(size * 0.22, 0.4, 2.2);
    case 'shell_midden': return clamp(size * 0.28, 0.6, 3.4);
    case 'cave_mouth': return clamp(size * 0.75, 2.5, 8);
    case 'bone_field': return clamp(size * 0.10, 0.4, 1.6);
    case 'spring': return clamp(size * 0.12, 0.3, 0.7);
  }
}
